import { useState } from 'react';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useCreateSubmittalItem,
  useUpdateSubmittalItem,
  useDeleteSubmittalItem,
} from '@/hooks/useSubmittals';
import type { SubmittalItem } from '@/hooks/useSubmittals';

interface SubmittalItemsListProps {
  submittalId: string;
  items: SubmittalItem[];
}

export function SubmittalItemsList({ submittalId, items }: SubmittalItemsListProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newItem, setNewItem] = useState({ description: '', manufacturer: '', model_number: '' });
  const [editItem, setEditItem] = useState({ description: '', manufacturer: '', model_number: '' });

  const createItem = useCreateSubmittalItem();
  const updateItem = useUpdateSubmittalItem();
  const deleteItem = useDeleteSubmittalItem();

  const handleAdd = async () => {
    if (!newItem.description.trim()) return;
    await createItem.mutateAsync({
      submittalId,
      item: {
        description: newItem.description.trim(),
        manufacturer: newItem.manufacturer.trim() || undefined,
        model_number: newItem.model_number.trim() || undefined,
      },
    });
    setNewItem({ description: '', manufacturer: '', model_number: '' });
    setIsAdding(false);
  };

  const handleUpdate = async (itemId: string) => {
    await updateItem.mutateAsync({
      submittalId,
      itemId,
      data: {
        description: editItem.description.trim() || undefined,
        manufacturer: editItem.manufacturer.trim() || undefined,
        model_number: editItem.model_number.trim() || undefined,
      },
    });
    setEditingId(null);
  };

  const startEdit = (item: SubmittalItem) => {
    setEditingId(item.id);
    setEditItem({
      description: item.description,
      manufacturer: item.manufacturer || '',
      model_number: item.model_number || '',
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          Items ({items.length})
        </span>
        {!isAdding && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Item
          </Button>
        )}
      </div>

      {items.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground">
                <th className="px-3 py-1.5 text-left w-10">#</th>
                <th className="px-3 py-1.5 text-left">Description</th>
                <th className="px-3 py-1.5 text-left">Manufacturer</th>
                <th className="px-3 py-1.5 text-left">Model #</th>
                <th className="px-3 py-1.5 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t hover:bg-muted/30 group">
                  {editingId === item.id ? (
                    <>
                      <td className="px-3 py-1.5 text-muted-foreground">{item.item_number}</td>
                      <td className="px-3 py-1">
                        <Input
                          value={editItem.description}
                          onChange={(e) => setEditItem(prev => ({ ...prev, description: e.target.value }))}
                          className="h-7 text-sm"
                        />
                      </td>
                      <td className="px-3 py-1">
                        <Input
                          value={editItem.manufacturer}
                          onChange={(e) => setEditItem(prev => ({ ...prev, manufacturer: e.target.value }))}
                          className="h-7 text-sm"
                        />
                      </td>
                      <td className="px-3 py-1">
                        <Input
                          value={editItem.model_number}
                          onChange={(e) => setEditItem(prev => ({ ...prev, model_number: e.target.value }))}
                          className="h-7 text-sm"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex gap-1">
                          <button className="p-1 hover:bg-muted rounded" onClick={() => handleUpdate(item.id)}>
                            <Check className="w-3 h-3 text-green-600" />
                          </button>
                          <button className="p-1 hover:bg-muted rounded" onClick={() => setEditingId(null)}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-1.5 text-muted-foreground">{item.item_number}</td>
                      <td className="px-3 py-1.5">{item.description}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{item.manufacturer || '—'}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{item.model_number || '—'}</td>
                      <td className="px-3 py-1.5">
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                          <button className="p-1 hover:bg-muted rounded" onClick={() => startEdit(item)}>
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            className="p-1 hover:bg-destructive/10 hover:text-destructive rounded"
                            onClick={() => deleteItem.mutate({ submittalId, itemId: item.id })}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdding && (
        <div className="border border-dashed border-blue-300 rounded-lg p-3 space-y-2">
          <Input
            placeholder="Description *"
            value={newItem.description}
            onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
            className="h-8 text-sm"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <div className="flex gap-2">
            <Input
              placeholder="Manufacturer"
              value={newItem.manufacturer}
              onChange={(e) => setNewItem(prev => ({ ...prev, manufacturer: e.target.value }))}
              className="h-8 text-sm"
            />
            <Input
              placeholder="Model #"
              value={newItem.model_number}
              onChange={(e) => setNewItem(prev => ({ ...prev, model_number: e.target.value }))}
              className="h-8 text-sm"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" className="h-7" onClick={() => setIsAdding(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7"
              onClick={handleAdd}
              disabled={!newItem.description.trim() || createItem.isPending}
            >
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
